<script setup lang="ts">
import { ref, computed, watchEffect } from "vue";
import { useBapi } from "@/bapi-client";
import NoteWriterSelector from "@/components/app/NoteWriter";
import { FilePreviewModal, ScrollActionList, CommentCard } from "@/components/common";
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import { capitalize } from "@/utils/text";
import PopoverTag from "@/components/common/PopoverTag.vue";
import { Button } from "@/components/ui/button";
import TicketDetailsAssetRow from "./TicketDetailsAssetRow.vue";
import TicketDetailsAssignee from "./TicketDetailsAssignee.vue";
import CloseTicket from "./CloseTicket.vue";
import { useAttachments } from "@/composables/attachments";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";
import { CSCreateNoteEvent } from "@/types/rudderstack";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { useGetTicket, useGetTicketNotes, useGetTicketNoteSignedUploadURL } from "@/api/queries/customerSupport";
import { useCreateTicketNote, useUpdateTicket } from "@/api/mutations/customerSupport";
import { TicketAttachmentRequest } from "@telegraphio/bapi-types";
import { mapNoteAttachment, buildUpdateTicketRequest } from "@/utils/customerSupportHelpers";

const { attachments, thumbnails, loadThumbnails, files, downloadFile, previewFile } = useAttachments();

interface Props {
  customerId: string;
  ticketId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: "assigned"): void;
}>();
const trackers = useTrackerStore();
const router = useRouter();

const title = defineModel<string>("title");

const { data: ticket } = useGetTicket(
  props.customerId,
  props.ticketId ?? "",
  computed(() => !!props.ticketId),
);
const { data: ticketNotes } = useGetTicketNotes(props.customerId, props.ticketId ?? "");
const {
  data: ticketNoteSignedUploadURL,
  isError: isErrorGettingTicketNoteSignedUploadURL,
  error: errorGettingTicketNoteSignedUploadURL,
} = useGetTicketNoteSignedUploadURL(props.customerId);

const createTicketNoteMutation = useCreateTicketNote();
const updateTicketMutation = useUpdateTicket();

const imagePreview = ref<{ fileId: string; noteId: string; fileName: string; fileType: string; src: string }>();
const showPreview = ref(false);
const isSaving = ref(false);
const isSaved = ref(false);
const failedToSave = ref(false);

interface NoteForm {
  text?: string;
  attachments?: FileList;
}

async function preview(noteId: string, fileId: string) {
  const file = attachments.value[noteId]?.find((att) => att.id === fileId);
  if (!file) return undefined;

  toast.loading("Loading preview");
  const srcURL = await previewFile(noteId, fileId);
  toast.dismiss();
  if (!srcURL) return undefined;
  imagePreview.value = {
    noteId,
    fileId,
    fileName: file.filename,
    fileType: file.mime_type,
    src: srcURL,
  };
  showPreview.value = true;
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_VIEW_ATTACHMENT, {
    attachment_name: file.filename,
  });
}

async function downloadAttachment(noteId: string, fileId: string) {
  const result = await downloadFile(noteId, fileId);
  if (!result) {
    return trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_DOWNLOAD_ATTACHMENT, {
      success: false,
    });
  }
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_DOWNLOAD_ATTACHMENT, { success: true });
}

async function submitNote(newNote: NoteForm): Promise<void> {
  failedToSave.value = false;
  isSaving.value = true;
  isSaved.value = false;
  if (!newNote.text && !newNote.attachments) {
    toast.warning("Note cannot be empty!");
    isSaving.value = false;
    isSaved.value = false;
    return;
  }
  if (!ticket.value?.data?.customer_id || !ticket.value?.data?.id || !ticket.value?.data?.id_prefix) {
    toast.warning("Ticket information is missing!");
    isSaving.value = false;
    isSaved.value = false;
    failedToSave.value = true;
    return;
  }
  const rsData: CSCreateNoteEvent = {
    ticket_id: props.ticketId,
    has_text: newNote.text ? true : false,
    attachment_count: newNote.attachments ? newNote.attachments.length : 0,
    success: false,
  };
  let noteAttachments: TicketAttachmentRequest[] = [];
  if (newNote.attachments) {
    const attachmentPromises = Array.from(newNote.attachments).map(async (attachment) => {
      if (isErrorGettingTicketNoteSignedUploadURL.value) {
        console.error("Failed to get signed upload url for attachment");
        toast.warning(`Could not save attachment: ${attachment.name}`);
        rsData.error = errorGettingTicketNoteSignedUploadURL.value ?? undefined;
        trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CREATE_NOTE, rsData);
        return null;
      } else {
        const uploadResponse = await useBapi(
          BAPI_COMMANDS.S3_UPLOAD_FILE,
          attachment,
          ticketNoteSignedUploadURL.value?.upload_url ?? "",
        );
        if (!uploadResponse.success) {
          console.error("Failed to upload file, received ", uploadResponse.error.status);
          toast.warning(`Could not save attachment: ${attachment.name}`);
          return null;
        }
        return {
          attachment_id: ticketNoteSignedUploadURL.value?.attachment_id ?? "",
          filename: attachment.name,
          mime_type: attachment.type,
          filesize: attachment.size,
          alt_text: attachment.name,
        };
      }
    });
    const uploadResults = await Promise.all(attachmentPromises);
    for (const res of uploadResults) {
      if (res !== null) {
        noteAttachments.push(res);
      }
    }
  }

  const result = await createTicketNoteMutation
    .mutateAsync({
      customerId: props.customerId,
      ticketId: props.ticketId,
      requestData: {
        note: newNote.text ?? "",
        attachments: noteAttachments,
      },
    })
    .catch(() => {
      isSaving.value = false;
      return false as const;
    });

  if (!result) return;

  toast.success("Ticket comment created!");
  isSaved.value = true;
  for (const note of result?.data ?? []) {
    if (!attachments.value[note.id]) {
      attachments.value[note.id] =
        note.attachments?.map((attachment) => mapNoteAttachment(attachment, note.user.user_id)) ?? [];
    }
  }
  isSaving.value = false;
  rsData.success = true;
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CREATE_NOTE, rsData);
}

const priorityIconClass = computed(() => {
  if (ticket.value?.data?.priority?.toLowerCase() === "high") return "text-red-400";
  if (ticket.value?.data?.priority?.toLowerCase() === "medium") return "text-orange-400";
  return "text-yellow-400";
});

function closePreview() {
  imagePreview.value = undefined;
  showPreview.value = false;
}

const loadedEmpty = computed(() => {
  return ticket.value?.data?.loaded_empty === "L"
    ? "Loaded"
    : ticket.value?.data?.loaded_empty === "E"
      ? "Empty"
      : "- -";
});

const railroad = computed(() => {
  if (ticket.value?.data?.railroads?.length && ticket.value?.data?.railroads?.length > 0)
    return ticket.value?.data?.railroads[0];
  return "- -";
});

const assignee = computed(() => {
  if (ticket.value?.data?.assignees?.length && ticket.value?.data?.assignees?.length > 0) {
    return `${ticket.value?.data?.assignees[0].first_name} ${ticket.value?.data?.assignees[0].last_name}`;
  }
  return "- -";
});

const firstTag = computed(() => {
  if (ticket.value?.data?.tags?.length && ticket.value?.data?.tags.length > 0) {
    return ticket.value?.data?.tags[0];
  }
  return "- -";
});

const moreTags = computed(() => {
  if (ticket.value?.data?.tags?.length && ticket.value?.data?.tags.length > 1) {
    return ticket.value?.data?.tags.slice(1);
  }
  return undefined;
});

async function closeTicket() {
  if (!ticket.value?.data) return;

  const requestData = buildUpdateTicketRequest(ticket.value.data, { state: "Closed" });

  const result = await updateTicketMutation
    .mutateAsync({
      customerId: props.customerId,
      ticketId: props.ticketId,
      requestData,
    })
    .catch(() => {
      trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CLOSE, { success: false });
      toast.warning(`Unable to close ticket.`);
      return false as const;
    });

  if (!result) return;

  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CLOSE, { success: true });
  toast.success(`TICKET-${props.ticketId} closed.`);
  router.push({
    name: "customerSupport",
    params: { customerId: props.customerId },
  });
}

function trackAssetClickThrough(asset_id: string, waybill_id: string) {
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_OPEN_ASSET_DETAILS, {
    asset_id,
    waybill_id,
  });
}

function assignTicket() {
  emit("assigned");
}

watchEffect(() => {
  if (ticket.value?.data) {
    title.value = ticket.value.data.name;
  }

  if (ticketNotes.value) {
    for (const note of ticketNotes.value.data ?? []) {
      if (!attachments.value[note.id]) {
        attachments.value[note.id] =
          note.attachments?.map((attachment) => mapNoteAttachment(attachment, note.user.user_id)) ?? [];
      }
    }
  }
});
</script>

<template>
  <div>
    <div class="border-border flex gap-3 border-b py-4">
      <span class="!mb-0 w-[12.5%] self-center text-sm font-semibold !text-gray-500">Status</span>
      {{ ticket?.data?.state && capitalize(ticket.data.state) }}
    </div>
    <div class="border-border grid grid-cols-2 grid-rows-3 gap-3 border-b py-4">
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Priority</span>
        <span>
          <i class="fa-solid fa-fire-flame-simple mr-1 text-lg" :class="priorityIconClass" />{{
            ticket?.data?.priority && capitalize(ticket.data.priority)
          }}
        </span>
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Creator</span>
        {{ ticket?.data?.created_by.first_name }} {{ ticket?.data?.created_by.last_name }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">L/E</span>
        {{ loadedEmpty }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Ticket #</span>
        {{ ticket?.data?.id }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Railroad</span>
        {{ railroad }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Date</span>
        {{ ticket?.data?.created_ts ? $date("MM/DD/YYYY", ticket?.data?.created_ts) : "" }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Tags</span>
        {{ firstTag }}
        <PopoverTag v-if="moreTags" name="tags" :label="`+ ${moreTags.length}`">
          <ul class="divide-y">
            <template v-for="tag in moreTags" :key="tag">
              <li class="px-3 py-1.5 text-sm font-semibold">{{ tag }}</li>
            </template>
          </ul>
        </PopoverTag>
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Assignee</span>
        {{ assignee }}
      </div>
    </div>
    <div v-if="ticket?.data?.ticket_assets?.length ?? 0 > 0" class="border-border flex flex-col gap-1.5 border-b py-3">
      <TicketDetailsAssetRow
        v-for="asset in ticket?.data?.ticket_assets"
        :key="asset.equipment_id ?? ''"
        :asset="asset"
        :customer-id="customerId"
        @open-asset="trackAssetClickThrough"
      />
    </div>
    <div v-if="ticket?.data?.description" class="border-border flex gap-3 border-b py-4">
      {{ ticket?.data?.description }}
    </div>
    <div
      v-if="ticket && ticket.data.state !== 'Closed'"
      class="border-border flex w-full items-center justify-between gap-3 border-b py-3"
    >
      <router-link :to="{ name: 'editTicket', params: { ticketId } }">
        <Button
          variant="secondary"
          @click="trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_START_EDIT, {})"
        >
          Edit ticket
        </Button>
      </router-link>
      <TicketDetailsAssignee
        :ticket="ticket.data"
        :class="{
          'ml-auto': !ticket.data.assignees?.length,
          'mr-auto': ticket.data.assignees?.length,
        }"
        @assigned="assignTicket"
      />
      <CloseTicket :ticket-name="ticket?.data?.name" @close-ticket="closeTicket" />
    </div>
    <div v-if="ticketNotes && ticketNotes.data?.length > 0" class="border-border flex gap-3 border-b py-4">
      <FilePreviewModal
        v-if="imagePreview"
        :is-active="showPreview"
        :file-type="imagePreview?.fileType"
        :file-name="imagePreview?.fileName"
        :url="imagePreview?.src"
        @download="downloadAttachment(imagePreview?.noteId, imagePreview.fileId)"
        @dismiss="closePreview"
      />
      <ScrollActionList class="pr-2" :show-divider="false" :scroll-down="false" @visible="loadThumbnails">
        <CommentCard
          v-for="note in ticketNotes.data ?? []"
          :key="note.id"
          :note="{
            id: note.id,
            waybill_id: '',
            equipment_id: '',
            user_id: note.user.user_id,
            note_text: note.note,
            created_ts: note.created_ts,
            updated_ts: '',
            customer_id: '',
            user_name: note.user.first_name + ' ' + note.user.last_name,
            user_initials: note.user.first_name[0] + note.user.last_name[0],
            company_name: '',
            attachments: note.attachments?.map((attachment) => mapNoteAttachment(attachment, note.user.user_id)) || [],
            signed_profile_pictures: note.user.profile_picture,
          }"
          :thumbnails="thumbnails[note.id]"
          :is-clickable="false"
          :files="files[note.id]"
          :show-attachments="true"
          @download-file="downloadFile(note.id, $event)"
          @preview-file="preview(note.id, $event)"
        />
      </ScrollActionList>
    </div>
    <div class="flex gap-3 py-4">
      <NoteWriterSelector
        use-legacy-note-writer
        :ticket-id="ticket?.data?.qualified_id ?? ''"
        :is-saving="isSaving"
        :is-saved="isSaved"
        @submit-note="submitNote"
      />
    </div>
  </div>
</template>
